import Dexie, { type DexieOptions, type EntityTable } from 'dexie';

import { assertMigrationsAreWellFormed, MIGRATIONS, SCHEMA_VERSION } from './migrations';
import type {
  Aircraft,
  Flight,
  LogbookCheck,
  LogbookEntry,
  Setting,
  Tombstone,
  Track,
  TrackFile,
  TrackPoints,
} from './types';

/**
 * The local database. Everything the pilot owns lives here and nowhere else —
 * there is no server, and there is no account.
 */
export class FlightDataViewerDb extends Dexie {
  declare tracks: EntityTable<Track, 'id'>;
  declare trackPoints: EntityTable<TrackPoints, 'trackId'>;
  declare trackFiles: EntityTable<TrackFile, 'id'>;
  declare flights: EntityTable<Flight, 'id'>;
  declare aircraft: EntityTable<Aircraft, 'id'>;
  declare settings: EntityTable<Setting, 'key'>;
  declare logbookEntries: EntityTable<LogbookEntry, 'id'>;
  declare logbookChecks: EntityTable<LogbookCheck, 'id'>;
  declare tombstones: EntityTable<Tombstone, 'key'>;

  /**
   * @param name  Database name. Tests pass their own so they never share state.
   * @param options  Dexie options — tests inject a fresh `indexedDB` factory here.
   */
  constructor(name = 'flight-data-viewer', options?: DexieOptions) {
    super(name, options);
    assertMigrationsAreWellFormed();

    for (const migration of MIGRATIONS) {
      const version = this.version(migration.version);
      if (Object.keys(migration.stores).length > 0) {
        version.stores(migration.stores);
      }
      if (migration.upgrade) {
        version.upgrade(migration.upgrade);
      }
    }
  }
}

export const db = new FlightDataViewerDb();

/** The schema version this build expects. Re-exported so callers need one import. */
export { SCHEMA_VERSION };

/**
 * `navigator.storage` is absent in older WebViews, in private modes and in
 * non-DOM test environments — the DOM types insist otherwise, so narrow it here
 * rather than scattering optional chaining the compiler believes is dead.
 */
function getStorageManager(): Partial<StorageManager> | undefined {
  return (globalThis as { navigator?: { storage?: Partial<StorageManager> } }).navigator?.storage;
}

/**
 * Ask the browser to stop treating the database as disposable cache.
 *
 * Without this, IndexedDB is evictable under storage pressure and a pilot can
 * lose a logbook to a full disk. Call it before the first write, not at startup:
 * Firefox and Safari prompt, and a prompt on an empty app is the wrong moment.
 *
 * @returns whether storage is persistent, or `null` if the browser has no opinion.
 */
export async function requestPersistentStorage(): Promise<boolean | null> {
  const storage = getStorageManager();
  if (!storage?.persist || !storage.persisted) {
    return null;
  }
  try {
    if (await storage.persisted()) {
      return true;
    }
    return await storage.persist();
  } catch {
    // A denied or unavailable permission is not worth surfacing here; the backup
    // reminder is the real safety net.
    return null;
  }
}
